import { all, cancel, fork, call, put, takeLatest, select } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import * as http from 'utils/http';
import config from 'config';
import _ from 'lodash';
import queryString from 'query-string';

import serialize from 'utils/serialize';
import history from 'modules/history';

import {
  setLoading,
  setPlainData,
  setGroupedData,
  setNextLink,
  setLastPage,
  setTotals,
  setMounted,
  setVisible,
  setPlainAndGroupedData,
  setLoadingNextPage,
} from './duck';

import { filter$ } from 'redux/modules/queued/effects';

import { TrackeableEvent } from 'redux/modules/analytics';
import { SHIPPERORDERS_LOAD } from 'redux/modules/analytics/events';

import { getFilters } from 'redux/modules/queued/selectors';

import { displayError } from 'redux/modules/error-handler/effects';
import { SHIPPERORDERS_FETCH } from 'redux/modules/error-handler/constants';

import { getIsVisible, getLastPage } from './selectors';

import { getVehiclesFilter, getRoutesFilter } from 'utils/filters';
import { createOrderRedirectConfig } from 'tenant/bussines';

const normalize = (data) => new Map(data.map((so) => [so.id, so]));

// @effect Action
const openModal$ = 'shipper-orders/openModal$';
const visibilityChange$ = 'shipper-orders/visibilityChange$';
const queuedMounted$ = 'shipper-orders/queuedMounted$';
const queuedUnmounted$ = 'shipper-orders/queuedUnmounted$';
const fetchInitialShipperOrdersPage$ = 'shipper-orders/fetchInitialShipperOrdersPage$';
const fetchNextShipperOrdersPage$ = 'shipper-orders/fetchNextShipperOrdersPage$';

// @effect Creators
export const visibilityChange = (visible) => ({
  type: visibilityChange$,
  payload: { visible },
});

export const openShipperOrderDetail = ({ id, forceRedirectToDetailOrder = false }) => ({
  type: openModal$,
  payload: {
    id,
    forceRedirectToDetailOrder,
  },
});

export const fetchInitialShipperOrdersPage = (filters) => ({
  type: fetchInitialShipperOrdersPage$,
  payload: filters,
});

export const queuedMounted = (filters) => ({
  type: queuedMounted$,
  payload: filters,
});

export const queuedUnmounted = () => ({
  type: queuedUnmounted$,
  payload: {},
});

export const fetchNextShipperOrdersPage = (nextPageLink) => ({
  type: fetchNextShipperOrdersPage$,
  payload: { nextPageLink },
});

// Workers
function* openModal({ payload }) {
  let url = createOrderRedirectConfig(payload.id);
  if (payload.forceRedirectToDetailOrder) {
    url = `/queued/${payload.id}`;
  }
  yield call(history.push, url);
}

const loadShipperOrdersTrackeableEvent = new TrackeableEvent();

const getPlainData = (state) => state.shipperOrders.plainData;

const groupShipperOrderData = (data) => {
  const groupedData = _.groupBy(data, ({ start_time_from }) => start_time_from.split('T')[0]);
  return Object.keys(groupedData).reduce((acc, currV) => {
    acc[currV] = groupedData[currV].map((i) => i.id);
    return acc;
  }, {});
};

const baseOrdering = [
  'trunc_pickup_time_from',
  'trunc_pickup_time_to',
  'trunc_dropoff_time_from',
  'trunc_dropoff_time_to',
  'created_at',
];

const mapFiltersForBackend = (filters) => {
  const {
    page,
    statuses,
    sorting,
    date,
    page_size,
    routes,
    vehicle_type,
    shipper,
    codi,
    xDock,
    cancelled,
  } = filters;

  const shipperFilter = shipper ? shipper.id : '';

  const ordering = !!date ? [sorting, ...baseOrdering].join(',') : baseOrdering.join(',');

  const vehicleTypesFilter = getVehiclesFilter(vehicle_type);
  const routesFilter = getRoutesFilter(routes);

  return {
    status: cancelled ? ['CANCELLED'] : statuses,
    ordering,
    page_size,
    shipper: shipperFilter,
    pickup_time_from: date,
    non_codi: codi ? String(codi) : undefined,
    xdock: xDock ?? undefined,
    page,
    ...vehicleTypesFilter,
    ...routesFilter,
  };
};

function* handleTotals(ownFilters) {
  const isVisible = yield select(getIsVisible);

  if (!isVisible) {
    return;
  }

  const _filters = yield select(getFilters);
  const filters = ownFilters || _filters;

  const queryStr = yield call(serialize, mapFiltersForBackend({ ...filters, date: undefined }));
  try {
    const totalsByDate = yield call(
      http.get,
      `${config.API_URL}mercury/count/shipper_orders/?${queryStr}`
    );

    yield put(setTotals(totalsByDate));
  } catch (err) {
    console.error(err);
  }
}

let pollingRealtimeTask = null;
const pollingRealtimeTimeout = 120000;

function* handleLastPage(next) {
  if (!next) {
    return;
  }

  const query = queryString.parse(next.split('?')[1]);
  const { page } = query;

  yield put(setLastPage(parseInt(page, 10)));
}

function* handleFetchShipperOrders({ payload }) {
  const { nextPageLink } = payload;

  if (nextPageLink) {
    yield fork(handleFetchNextShipperOrders, { payload });
    return;
  }

  const filters = { ...payload, page_size: 20 };

  if (pollingRealtimeTask) {
    yield cancel(pollingRealtimeTask);
  }

  const queryStr = yield call(serialize, mapFiltersForBackend(filters));

  yield put(setLastPage(null));
  yield fork(handleTotals, filters);
  yield put(setLoading(true));

  try {
    yield call(loadShipperOrdersTrackeableEvent.start, SHIPPERORDERS_LOAD);
    const { next, results } = yield call(
      http.get,
      `${config.API_URL}mercury/shipper_orders/?${queryStr}`
    );

    const shipperOrders = results;
    const normalized = yield call(normalize, results);
    const groupedData = yield call(groupShipperOrderData, shipperOrders);

    yield put(setPlainData(normalized));
    yield put(setGroupedData(groupedData));

    yield put(setNextLink(next));
    yield put(setLastPage(1));

    pollingRealtimeTask = yield fork(pollingRealtime, filters);
  } catch (err) {
    console.error(err);
    yield put(displayError(err, SHIPPERORDERS_FETCH));
  } finally {
    yield put(setLoading(false));
    yield call(loadShipperOrdersTrackeableEvent.end, SHIPPERORDERS_LOAD);
  }
}

function* handleFetchNextShipperOrders({ payload }) {
  const { nextPageLink } = payload;

  if (!nextPageLink) {
    return;
  }

  try {
    yield put(setLoadingNextPage(true));

    const { next, results } = yield call(http.get, nextPageLink);
    const shipperOrders = results;

    yield put(setNextLink(next));
    yield call(handleLastPage, nextPageLink);

    const plainData = yield select(getPlainData);
    const newShipperOrders = [...plainData.values(), ...shipperOrders];
    const normalized = yield call(normalize, newShipperOrders);

    yield put(setPlainData(normalized));

    const groupedData = yield call(groupShipperOrderData, newShipperOrders);

    yield put(setGroupedData(groupedData));
  } catch (err) {
    console.error(err);
  } finally {
    yield put(setLoadingNextPage(false));
  }
}

const getPagesToRequest = (numberOfPages, filters) => {
  const urls = new Array(numberOfPages)
    .fill()
    .map((__, n) => serialize(mapFiltersForBackend({ ...filters, page: n + 1 })))
    .map((queryStr) => `${config.API_URL}mercury/shipper_orders/?${queryStr}`);

  return urls;
};

function* handleRealtime(filters) {
  const isVisible = yield select(getIsVisible);
  const lastPage = yield select(getLastPage);

  if (!isVisible || !lastPage) {
    return;
  }

  const urls = yield call(getPagesToRequest, lastPage, filters);

  const allResults = yield all(urls.map((url) => call(http.get, url)));

  const shipperOrders = _.flatten(allResults.map(({ results }) => results));

  const normalized = yield call(normalize, shipperOrders);
  const groupedData = yield call(groupShipperOrderData, shipperOrders);

  yield put(setPlainAndGroupedData({ plainData: normalized, groupedData }));
}

let pollingForTotalsTask = null;
const pollingForTotalsTimeout = 30000;

function* pollingForTotals() {
  try {
    while (true) {
      yield fork(handleTotals);
      yield call(delay, pollingForTotalsTimeout);
    }
  } catch (err) {
    console.error(err);
  }
}

function* pollingRealtime(filters) {
  try {
    while (true) {
      yield call(delay, pollingRealtimeTimeout);
      yield fork(handleRealtime, filters);
    }
  } catch (err) {
    console.error(err);
  }
}

function* handleVisibilityChange({ payload: { visible } }) {
  yield put(setVisible(visible));
}

function* handleMounted() {
  yield put(setMounted(true));

  pollingForTotalsTask = yield fork(pollingForTotals);
}

function* handleUnmounted() {
  if (pollingForTotalsTask) {
    yield cancel(pollingForTotalsTask);
  }
  if (pollingRealtimeTask) {
    yield cancel(pollingRealtimeTask);
  }

  yield put(setMounted(false));
}

// Watchers
export function* watchShipperOrders() {
  yield takeLatest(openModal$, openModal);
  yield takeLatest(visibilityChange$, handleVisibilityChange);

  yield takeLatest(
    [fetchInitialShipperOrdersPage$, filter$, fetchNextShipperOrdersPage$],
    handleFetchShipperOrders
  );

  yield takeLatest(queuedMounted$, handleMounted);
  yield takeLatest(queuedUnmounted$, handleUnmounted);
}
